import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext, invitationLoadedFor } from '../../../Contexts/AuthContext';
import Button from '../../../Components/Atoms/Button';
import TextField from '../../../Components/Atoms/TextField';
import Typography from '../../../Components/Atoms/Typography';
import LoadingScreen from '../../../Components/Atoms/LoadingScreen';
import InvitationLayout from '../../Components/InvitationLayout';
import { PROBLEM_TEXT } from '../../../utils/requestStatus';

// Asks for the name shown on the request when the account does not have one.
const SendRequest = () => {
  const { invitationId = '' } = useParams();
  const { t } = useTranslation('helpersAndErrors');
  const { invitation, init, authReady, user, sendRequest, signOutUser } = useContext(AuthContext);
  const [name, setname] = useState(user?.displayName ?? '');
  const [error, seterror] = useState<string | null>(null);
  const [busy, setbusy] = useState(false);
  const running = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (invitation.id !== invitationId) init(invitationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId]);

  const invalid = invitationLoadedFor(invitation, invitationId) && invitation.invalid;
  useEffect(() => {
    if (invalid) navigate(`/${invitationId}/error`, { replace: true });
  }, [invalid, invitationId, navigate]);

  useEffect(() => {
    if (authReady && !user) navigate(`/${invitationId}`, { replace: true });
  }, [authReady, user, invitationId, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (running.current) return;
    if (name.trim().length < 3) {
      seterror('Please enter your full name');
      return;
    }
    running.current = true;
    setbusy(true);
    const { problem } = await sendRequest(name);
    running.current = false;
    setbusy(false);
    if (problem) seterror(PROBLEM_TEXT[problem]);
    else navigate(`/${invitationId}/waitingForAcceptance`);
  };

  if (!invitationLoadedFor(invitation, invitationId) || invalid || !authReady || !user) return <LoadingScreen loading />;

  return (
    <InvitationLayout>
      <Typography variant='h3' className='invitation-title' translation='common'>
        Send request
      </Typography>
      <Typography variant='text-2' color='subdued' className='invitation-lead' translation={null}>
        {t('Your name is shown to {{restaurant}} with the request.', { restaurant: invitation.restaurantName ?? '' })}
      </Typography>
      <form className='invitation-form' onSubmit={onSubmit} noValidate>
        <TextField
          fullWidth
          className='form-fields'
          name='name'
          autoComplete='name'
          value={name}
          error={!!error}
          helperText={error ? t(error) : ''}
          onChange={(e) => {
            setname(e.target.value);
            seterror(null);
          }}
          label='Enter name'
          labelTranslation='helpersAndErrors'
        />
        <div className='invitation-actions'>
          <Button fullWidth type='submit' variant='primary' translation='common' loading={busy} disabled={busy}>
            Send request
          </Button>
          <Button fullWidth variant='transparent' translation='helpersAndErrors' onClick={signOutUser}>
            Use another account
          </Button>
        </div>
      </form>
    </InvitationLayout>
  );
};

export default SendRequest;
